/**
 * Section Operations Utility
 * 
 * Helper functions to create section insertion operations.
 */

import type { DocumentOperation, InsertSectionOp } from '../types/document-operations';
import type { DocumentSection, SectionType } from '../types/document-schema';

/**
 * Create an operation to insert a new section
 * 
 * @param sectionType - Type of section to create (section, subsection, appendix)
 * @param title - Section title
 * @param targetSectionId - ID of section to insert relative to
 * @param position - Where to insert ('before' or 'after')
 * @param parentId - Optional parent section ID for subsections
 */
export function createInsertSectionOperation(
  sectionType: 'section' | 'subsection' | 'appendix',
  title: string,
  targetSectionId?: string,
  position: 'before' | 'after' = 'after',
  parentId?: string
): DocumentOperation {
  const sectionId = `sec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const newSection: DocumentSection = {
    id: sectionId,
    type: sectionType as SectionType,
    number: '', // Will be computed by backend
    title,
    parent: parentId,
    children: [],
    blocks: [],
    required: false,
    createdAt: new Date().toISOString(),
    createdBy: 'user', // TODO: Get from auth context
    lastModified: new Date().toISOString(),
    modifiedBy: 'user',
  };
  
  // Create the appropriate operation based on position
  const op = position === 'after' ? 'insert_section_after' : 'insert_section_before';
  
  return {
    op: op as 'insert_section_after' | 'insert_section_before',
    targetId: targetSectionId || 'root',
    newSection,
  } as InsertSectionOp;
}

/**
 * Create operation to append a section at the end of document
 */
export function createAppendSectionOperation(
  sectionType: 'section' | 'subsection' | 'appendix',
  title: string,
  parentId?: string
): DocumentOperation {
  const sectionId = `sec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const newSection: DocumentSection = {
    id: sectionId,
    type: sectionType as SectionType,
    number: '',
    title,
    parent: parentId,
    children: [],
    blocks: [],
    required: false,
    createdAt: new Date().toISOString(),
    createdBy: 'user',
    lastModified: new Date().toISOString(),
    modifiedBy: 'user',
  };
  
  return {
    op: 'insert_section_after',
    targetId: 'last', // Special marker for last section
    newSection,
  } as InsertSectionOp;
}

/**
 * Create operation to delete a section
 */
export function createDeleteSectionOperation(
  sectionId: string,
  allowRequired: boolean = false
): DocumentOperation {
  return {
    op: 'delete_section',
    targetId: sectionId,
    allowRequired,
  };
}

/**
 * Create operation to update section title
 */
export function createUpdateSectionTitleOperation(
  sectionId: string,
  newTitle: string
): DocumentOperation {
  return {
    op: 'update_section_title',
    targetId: sectionId,
    newTitle,
  };
}
